<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings } = useUISettings();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta || {});

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() => {
  const senderId = props.chat.meta?.sender?.id;
  return senderId ? store.getters['contacts/getContact'](senderId) || {} : {};
});

const isGroupConversation = computed(() => props.chat.group === true);

const displayName = computed(() => {
  if (isGroupConversation.value) {
    return props.chat.group_title || t('CONVERSATION.GROUP.DEFAULT_TITLE');
  }

  return currentContact.value.name || props.chat.meta?.sender?.name || '';
});

const groupMemberCount = computed(() => {
  const groupContacts = props.chat.group_contacts || [];
  const primaryMemberCount = props.chat.meta?.sender?.id ? 1 : 0;
  return groupContacts.length + primaryMemberCount;
});

const groupMemberCountLabel = computed(() =>
  t('CONVERSATION.GROUP.MEMBER_COUNT', { count: groupMemberCount.value })
);

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const conversationDisplayId = computed(
  () => currentChat.value?.display_id || currentChat.value?.id
);

const iungoStatusLabel = computed(() => {
  if (inbox.value?.channel_type !== 'Channel::Iungo') {
    return '';
  }
  const attrs = inbox.value?.additional_attributes || {};
  const typingValue = attrs.gupshup_send_typing_indicator;
  const readValue = attrs.gupshup_auto_mark_read;
  const typingOn = typingValue === true || typingValue === 'true';
  const readOn = readValue === true || readValue === 'true';
  if (!typingOn && !readOn) {
    return '';
  }
  const parts = [];
  if (readOn) parts.push('Lido Automaticamente');
  if (typingOn) parts.push('Digitando');
  return `Iungo: ${parts.join('/')} ON`;
});

const openContactPanel = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 pt-3 pb-2 h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <div
        v-if="isGroupConversation"
        class="flex items-center justify-center w-8 h-8 rounded-full bg-n-alpha-2 flex-shrink-0"
      >
        <span class="i-lucide-users text-sm text-n-slate-11" />
      </div>
      <Avatar
        v-else
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        hide-offline-status
        rounded-full
      />
      <div
        class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
      >
        <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
          <button
            v-if="!isGroupConversation"
            type="button"
            class="text-sm font-medium truncate leading-tight text-n-slate-12 hover:underline text-left"
            @click="openContactPanel"
          >
            {{ displayName }}
          </button>
          <span
            v-else
            class="text-sm font-medium truncate leading-tight text-n-slate-12"
          >
            {{ displayName }}
          </span>
          <fluent-icon
            v-if="!isHMACVerified && !isGroupConversation"
            v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
            size="14"
            class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
            icon="warning"
          />
        </div>

        <div
          class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
        >
          <span v-if="isGroupConversation" class="text-n-slate-11">
            {{ groupMemberCountLabel }}
          </span>
          <span
            v-if="isGroupConversation && hasMultipleInboxes"
            class="text-n-slate-10"
          >
            {{ $t('CONVERSATION.GROUP.SEPARATOR') }}
          </span>
          <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
          <span
            v-if="iungoStatusLabel && !isGroupConversation"
            class="inline-flex items-center rounded-full bg-n-alpha-2 px-2 py-0.5 text-[11px] font-semibold text-n-slate-12"
          >
            {{ iungoStatusLabel }}
          </span>
          <span v-if="isSnoozed" class="font-medium text-n-amber-10">
            {{ snoozedDisplayText }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <span
        v-if="conversationDisplayId"
        class="text-xs font-semibold text-n-slate-11 whitespace-nowrap"
      >
        #{{ conversationDisplayId }}
      </span>
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
